import FullHeader from "@/components/body/headers/FullHeader";
import MinimalHeader from "@/components/body/headers/MinimalHeader";
import DesktopFull from "@/components/body/layouts/DesktopFull";
import DesktopMin from "@/components/body/layouts/DesktopMin";
import MobileFull from "@/components/body/layouts/MobileFull";
import MobileMin from "@/components/body/layouts/MobileMin";
import NavMenu from "@/components/NavMenu";
import Popups from "@/components/popups/";

import Layout from "@/mixins/Layout";
import "@/plugins/svg.foreignobject.js";
import { navigationNamespace } from "@/store/modules/navigation";
import { CensusPreference } from "@/store/modules/navigation/types";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { towerNamespace } from "@/store/modules/tower";
import { TowerAction } from "@/store/modules/tower/actions";
import { TOWER_REVIEW_SAVE } from "@/store/modules/tower/mutation_types";
import { userNamespace } from "@/store/modules/user";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { BodyComponent, HeaderComponent } from "@/views/SharedViewStructures";

const BODY_POLLING_INTERVAL = 60000;
const BODY_FAST_POLLING_INTERVAL = 10000;
@Component({
  components: {
    DesktopFull,
    DesktopMin,
    MobileFull,
    MobileMin,
    FullHeader,
    MinimalHeader,
    NavMenu,
    ...Popups
  },
  beforeRouteLeave(_to: any, _from: any, next: Function) {
    const singlePatientView = this.$store.getters[
      `${patientNamespace.namespace}/singlePatientView`
    ];
    const path = _to.path.split("/");
    const toHelp = path.length > 2 && path[2] === "help";
    next(!singlePatientView || toHelp);
  }
})
export default class Body extends mixins(Layout) {
  // Releases lock when tower popup calls back on close
  @Action(TOWER_REVIEW_SAVE, towerNamespace)
  [TOWER_REVIEW_SAVE]!: Function;

  @Action("fetchBodyData", patientNamespace)
  fetchBodyData!: Function;

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  @Action("setHelpStep", navigationNamespace)
  setHelpStep!: Function;

  @Getter("demo", userNamespace)
  demo!: boolean;

  @Getter("isMask", userNamespace)
  isMask!: boolean;

  @Getter("censusPreferences", navigationNamespace)
  censusPreferences!: Record<string, CensusPreference>;

  @Getter("helpStep", navigationNamespace)
  helpStep!: number;

  @Getter("darkMode", navigationNamespace)
  dark!: boolean;

  @Getter("config", patientNamespace)
  config!: any;

  @Prop(Number)
  room!: number;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("mobilePage", navigationNamespace)
  _mobilePage!: number;

  @Action("setMobilePage", navigationNamespace)
  setMobilePage!: Function;

  @Getter("isWirelessTesting", userNamespace)
  isWirelessTesting!: boolean;

  @Getter("isTesting", patientNamespace)
  isTesting!: boolean;

  @Getter("hasWaves", patientNamespace)
  hasWaves!: boolean;

  @Getter("showStaff", patientNamespace)
  showStaff!: boolean;

  @Getter("current", patientNamespace)
  patient!: Patient;

  @Getter("minDataPatient", patientNamespace)
  minDataPatient!: boolean;

  @Getter("showGreenGrayOnly", userNamespace)
  showGreenGrayOnly!: any;

  get bodyComponent(): BodyComponent {
    const componentName = `${this.deviceType}${this.dataLayout}`;
    // Avoid a magic string that is hard to search for
    // created by concating these two computed values
    switch (componentName) {
      case BodyComponent.MobileMin:
        return BodyComponent.MobileMin;
      case BodyComponent.MobileFull:
        return BodyComponent.MobileFull;
      case BodyComponent.DesktopMin:
        return BodyComponent.DesktopMin;
      default:
        return BodyComponent.DesktopFull;
    }
  }

  get headerComponent(): HeaderComponent {
    if (this.patient && !this.minDataPatient) {
      return HeaderComponent.FullHeader;
    } else {
      return HeaderComponent.MinimalHeader;
    }
  }

  get mobilePage() {
    return this._mobilePage;
  }

  // Used by mobile navigation component
  set mobilePage(value: number) {
    this.setMobilePage(value);
  }

  @Watch("room")
  @Watch("isMask")
  routeChange() {
    this.fetchWithCheck();
  }

  private startPolling() {
    if (this.interval) {
      // Avoid setting up double polling
      return;
    }
    this.interval = setInterval(() => {
      this.fetchWithCheck();
    }, this.pollIntervalInMs);
  }

  private stopPolling() {
    clearInterval(this.interval);
    this.interval = null;
  }

  @Watch("isWirelessTesting")
  @Watch("isTesting")
  async changePollingInterval() {
    this.stopPolling();
    this.startPolling();
  }

  get pollIntervalInMs() {
    return this.isWirelessTesting || this.isTesting
      ? BODY_FAST_POLLING_INTERVAL
      : BODY_POLLING_INTERVAL;
  }

  isDestroyed = false;

  async fetchWithCheck() {
    if (this.isDestroyed) {
      return;
    }
    await this.fetchBodyData(this.room).then(() => {
      if (this.isDestroyed) {
        return;
      }
      const regNum = _.get(this, "patient.census.regNum", "");
      if (regNum === "") {
        this.back();
      }
    });
  }

  interval: any = null;

  async mounted() {
    this.setMobilePage(1);

    const ref = this;
    window.awCommunicationChannel.releaseLockAndRefreshData = async function(
      roomSys: string
    ) {
      if (roomSys) {
        await ref[TOWER_REVIEW_SAVE]({
          item: { claim: 0 },
          action: TowerAction.EDIT_CASE_REVIEW,
          roomSys
        });
      }
      await ref.fetchWithCheck();
    };

    // initial
    await this.fetchWithCheck();

    if (this.isDestroyed) {
      return;
    }

    this.startPolling();
  }

  back() {
    const area = this.area;
    let params: Record<string, string> = {
      area
    };
    // Restore view / unit if we came from another census
    const previousRoute = this.censusPreferences[this.area];
    if (previousRoute) {
      Object.assign(params, previousRoute);
    }
    this.$router.push({
      name: "census",
      params
    });
  }

  beforeDestroy() {
    clearInterval(this.interval);
    this.isDestroyed = true;

    const openedWindows = _.get(window, "awCommunicationChannel.openedWindows");
    if (openedWindows) {
      Object.keys(openedWindows).forEach(key => {
        if (openedWindows[key] && openedWindows[key].close) {
          openedWindows[key].close();
        }
      });
    }
  }

  incrementStep() {
    const step = this.helpStep;
    if (step === 2) {
      this.setHelpStep(0);
    } else {
      this.setHelpStep(step + 1);
    }
  }

  get legendItems() {
    if (this.showGreenGrayOnly) {
      return this.config.body.patientLegend;
    } else {
      return this.config.body.legend;
    }
  }

  // Used to redraw the footer when resolution changes
  // this causes the app to resize if the footer resizes
  footerKeyIncrementOnResizeWithDebouncing = 1;

  // Debouncing shouldn't be this hard, but it works
  incrementValue = _.debounce((that, toIncrement) => {
    that[toIncrement]++;
  }, 100);

  @Watch("$vuetify.breakpoint.height")
  @Watch("$vuetify.breakpoint.width")
  onResize() {
    this.incrementValue(this, "footerKeyIncrementOnResizeWithDebouncing");
  }
}
